import React, { useState, useEffect } from 'react';
import Loader from '../../components/Loader';
import Calling from '../../api/Calling';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import generateCaptcha from '../../components/captcha';
import FileUpload from '../../components/UI/FileUpload';
import NotFound from '../../components/UI/NotFound';
import { imageType, MAX_FILE_SIZE_IMAGE, invalidImageMessage, imageSizeExceededMessage } from '../../components/imageType';
import { FaImage, FaUpload, FaRegEye, FaLink, FaEdit, FaTrashAlt, FaTimes } from 'react-icons/fa';
import { HiOutlineRefresh } from 'react-icons/hi';

const AiModel = () => {
    const [loader, setLoader] = useState(false);
    const [models, setModels] = useState([]);
    const [selectedFile, setSelectedFile] = useState(null);
    const [modelName, setModelName] = useState('');
    const [modelUrl, setModelUrl] = useState('');
    const [error, setError] = useState(null);
    const [editModel, setEditModel] = useState(null);
    const [modelStatus, setModelStatus] = useState(true);
    const [previewImage, setPreviewImage] = useState(null);
    const [fileProcessing, setFileProcessing] = useState({});
    const [fileStatus, setFileStatus] = useState({});
    const [selectedModels, setSelectedModels] = useState([]);
    const [showPreview, setShowPreview] = useState(false);
    const navigate = useNavigate();

    useEffect(() => {
        fetchModels();
    }, []);

    const fetchModels = async () => {
        setLoader(true);
        try {
            const resp = await Calling.getAiModel();
            if (resp?.status) {
                setModels(resp?.data);
            } else {
                toast.error(resp?.message);
            }
        } catch (error) {
            console.error("Fetch error:", error.response?.data || error.message);
            toast.error("Failed to fetch AI models");
        } finally {
            setLoader(false);
        }
    };

    const handleFileChange = (event) => {
        const file = event.target.files[0];
        const fieldName = event.target.name || 'image';

        setError(null);
        setSelectedFile(null);
        setFileProcessing({ ...fileProcessing, [fieldName]: true });
        setFileStatus({ ...fileStatus, [fieldName]: null });
        setPreviewImage(null);

        if (!file) {
            setError('No file selected.');
            setFileProcessing({ ...fileProcessing, [fieldName]: false });
            setFileStatus({ ...fileStatus, [fieldName]: 'error' });
            return;
        }

        if (!imageType.includes(file.type)) {
            setError(invalidImageMessage);
            toast.error(invalidImageMessage);
            setFileProcessing({ ...fileProcessing, [fieldName]: false });
            setFileStatus({ ...fileStatus, [fieldName]: 'error' });
            return;
        }

        if (file.size > MAX_FILE_SIZE_IMAGE) {
            setError(imageSizeExceededMessage);
            toast.error(imageSizeExceededMessage);
            setFileProcessing({ ...fileProcessing, [fieldName]: false });
            setFileStatus({ ...fileStatus, [fieldName]: 'error' });
            return;
        }

        setSelectedFile(file);
        setFileProcessing({ ...fileProcessing, [fieldName]: false });
        setFileStatus({ ...fileStatus, [fieldName]: 'success' });

        // Create preview URL for the selected file
        const previewUrl = URL.createObjectURL(file);
        setPreviewImage(previewUrl);
        setShowPreview(true);
    };

    const resetForm = () => {
        setSelectedFile(null);
        setModelName('');
        setModelUrl('');
        setError(null);
        setFileStatus({});
        setEditModel(null);
        setPreviewImage(null);
        setShowPreview(false);
        setModelStatus(true);
    };

    const handleSubmit = async (e, modelId = null) => {
        e.preventDefault();

        if (!modelId && (!selectedFile || !modelName || !modelUrl)) {
            setError('Please fill all required fields.');
            return;
        }

        // For edit mode, at least one field should be provided
        if (modelId && !selectedFile && !modelName && !modelUrl) {
            setError('Please update at least one field.');
            return;
        }

        setLoader(true);
        try {
            const formData = new FormData();
            if (selectedFile) {
                formData.append('image', selectedFile);
            }
            if (modelName) {
                formData.append('m_name', modelName);
            }
            if (modelUrl) {
                formData.append('m_url', modelUrl);
            }

            // Include status for edit mode
            if (modelId) {
                formData.append('m_status', modelStatus);
            }

            let resp;
            if (modelId) {
                resp = await Calling.updateAiModel(modelId, formData);
            } else {
                resp = await Calling.addAiModel(formData);
            }

            if (resp?.status) {
                toast.success(resp?.message);
                resetForm();
            } else {
                toast.error(resp?.message);
            }
        } catch (error) {
            console.error("Submission error:", error.response?.data || error.message);
            toast.error("Something went wrong.");
        } finally {
            setLoader(false);
            await fetchModels();
        }
    };

    const handleEdit = (model) => {
        setEditModel(model._id);
        setModelName(model.m_name);
        setModelUrl(model.m_url);
        setModelStatus(model.m_status);
        setPreviewImage(model.m_image);
        setShowPreview(true);
        setFileStatus({});
    };

    const handleDelete = async () => {
        if (selectedModels.length === 0) {
            toast.error('Please select at least one model to delete.');
            return;
        }

        const content = "You are about to delete selected items. To confirm, please solve this math problem.";
        const check = generateCaptcha(content);
        if (!check) return toast.error('Incorrect answer.');

        setLoader(true);
        try {
            const resp = await Calling.deleteAiModel({ id: selectedModels });
            if (resp?.status) {
                toast.success(resp?.message);
                setModels(models.filter((model) => !selectedModels.includes(model._id)));
                setSelectedModels([]);
            } else {
                toast.error(resp?.message);
            }
        } catch (error) {
            console.error("Delete error:", error.response?.data || error.message);
            toast.error("Failed to delete models.");
        } finally {
            setLoader(false);
        }
    };

    const handleUpdateStatus = async (id) => {
        const content = "You are about to update this model's status. To confirm, please solve this math problem.";
        const check = generateCaptcha(content);
        if (!check) return toast.error('Incorrect answer.');

        setLoader(true);
        try {
            // Get the current model
            const currentModel = models.find(model => model._id === id);
            // Create formData with updated status (toggled)
            const formData = new FormData();
            formData.append('m_status', !currentModel.m_status);
            formData.append('m_name', currentModel.m_name);
            formData.append('m_url', currentModel.m_url);

            const resp = await Calling.updateAiModel(id, formData);
            if (resp?.status) {
                toast.success(resp?.message);
                fetchModels();
            } else {
                toast.error(resp?.message);
            }
        } catch (error) {
            console.error("Status update error:", error.response?.data || error.message);
            toast.error("Failed to update status.");
        } finally {
            setLoader(false);
        }
    };

    const handleSelectModel = (id) => {
        if (selectedModels.includes(id)) {
            setSelectedModels(selectedModels.filter(modelId => modelId !== id));
        } else {
            setSelectedModels([...selectedModels, id]);
        }
    };

    const handleSelectAll = (e) => {
        if (e.target.checked) {
            setSelectedModels(models.map(model => model._id));
        } else {
            setSelectedModels([]);
        }
    };

    const closePreview = () => {
        setShowPreview(false);
    };

    return (
        <>
            {loader ? (
                <Loader loader={loader} />
            ) : (
                <div className="w-full min-h-screen px-4 py-6 mt-16 sm:px-6 lg:px-8 lg:ml-[4.4rem]">
                    <div className="mx-auto">
                        {/* Header */}
                        <div className="flex justify-between items-center mb-4">
                            <div>
                                <h1 className="text-2xl font-bold text-gray-900 sm:text-3xl">AI Model Management</h1>
                                <p className="mt-1 text-sm text-gray-500">Manage AI models available in your application</p>
                            </div>
                            <button
                                onClick={fetchModels}
                                className="flex items-center gap-2 px-4 py-2 bg-main-blue hover:bg-main-blue/80 rounded-lg text-white transition-colors duration-200 shadow-sm"
                            >
                                <HiOutlineRefresh className="text-lg" />
                                <span>Refresh</span>
                            </button>
                        </div>

                        {/* Add AI Model Card */}
                        <div className="bg-white rounded-xl shadow-sm overflow-hidden transition-all duration-300 hover:shadow-md border border-gray-100 mb-6">
                            <div className="p-5 border-b border-gray-100">
                                <div className="flex items-center gap-3">
                                    <div className="p-2.5 bg-main-blue/10 rounded-lg">
                                        <FaUpload className="text-main-blue text-xl" />
                                    </div>
                                    <div>
                                        <h2 className="text-lg font-semibold text-gray-800">Add AI Model</h2>
                                        <p className="text-xs text-gray-500 mt-0.5">Add new AI model to your application</p>
                                    </div>
                                </div>
                            </div>

                            <div className="p-5 bg-gray-50/50">
                                <form onSubmit={(e) => handleSubmit(e, editModel)} className="space-y-4">
                                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                        <div className="md:col-span-1">
                                            <div className="flex items-center gap-2 mb-1">
                                                <FaImage className="text-main-blue text-sm" />
                                                <label className="block text-sm font-medium text-gray-700">
                                                    Model Image
                                                </label>
                                            </div>
                                            <p className="text-xs text-gray-500 mb-2">Upload model icon or logo</p>
                                        </div>
                                        <div className="md:col-span-2">
                                            <div className="relative">
                                                <FileUpload
                                                    name="m_image"
                                                    id="image"
                                                    onChange={handleFileChange}
                                                    accept={imageType.join(',')}
                                                    required={!editModel}
                                                    multiple={false}
                                                    className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-main-blue transition duration-200"
                                                />
                                                {fileProcessing['image'] && (
                                                    <div className="absolute right-2 top-1/2 transform -translate-y-1/2">
                                                        <div className="loader"></div>
                                                    </div>
                                                )}
                                                {fileStatus['image'] === 'error' && (
                                                    <div className="absolute right-2 top-1/2 transform -translate-y-1/2 text-xl text-red-500">✘</div>
                                                )}
                                                {fileStatus['image'] === 'success' && (
                                                    <div className="absolute right-2 top-1/2 transform -translate-y-1/2 text-xl text-green-500">✓</div>
                                                )}
                                            </div>
                                            <p className="text-xs mt-1 text-gray-400">Allow png, jpg, jpeg / Max size: {MAX_FILE_SIZE_IMAGE / (1024 * 1024)} MB</p>

                                            {/* Image Preview */}
                                            {showPreview && previewImage && (
                                                <div className="mt-3 relative">
                                                    <div className="border rounded-lg p-2 inline-block relative">
                                                        <button
                                                            type="button"
                                                            onClick={closePreview}
                                                            className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600 transition-colors"
                                                        >
                                                            <FaTimes size={12} />
                                                        </button>
                                                        <img
                                                            src={previewImage}
                                                            alt="Preview"
                                                            className="h-24 w-24 object-contain rounded"
                                                        />
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    </div>

                                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                        <div className="md:col-span-1">
                                            <div className="flex items-center gap-2 mb-1">
                                                <FaEdit className="text-main-blue text-sm" />
                                                <label className="block text-sm font-medium text-gray-700">
                                                    Model Name
                                                </label>
                                            </div>
                                            <p className="text-xs text-gray-500 mb-2">Enter AI model name</p>
                                        </div>
                                        <div className="md:col-span-2">
                                            <input
                                                type="text"
                                                name="modelName"
                                                id="modelName"
                                                value={modelName}
                                                onChange={(e) => setModelName(e.target.value)}
                                                required={!editModel}
                                                className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-main-blue/80 transition duration-200"
                                                placeholder="e.g., DALL-E, Stable Diffusion"
                                            />
                                        </div>
                                    </div>

                                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                        <div className="md:col-span-1">
                                            <div className="flex items-center gap-2 mb-1">
                                                <FaLink className="text-main-blue text-sm" />
                                                <label className="block text-sm font-medium text-gray-700">
                                                    Model URL
                                                </label>
                                            </div>
                                            <p className="text-xs text-gray-500 mb-2">Enter model API or website URL</p>
                                        </div>
                                        <div className="md:col-span-2">
                                            <input
                                                type="url"
                                                name="modelUrl"
                                                id="modelUrl"
                                                value={modelUrl}
                                                onChange={(e) => setModelUrl(e.target.value)}
                                                required={!editModel}
                                                className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-main-blue transition duration-200"
                                                placeholder="https://example.com/api"
                                            />
                                            <p className={`text-xs mt-2 ${error ? 'text-red-500' : 'text-gray-500'}`}>{error ? error : ''}</p>
                                        </div>
                                    </div>

                                    <div className="flex items-center justify-end gap-3 pt-2">
                                        <button
                                            type="button"
                                            onClick={resetForm}
                                            className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 bg-white hover:bg-gray-50 transition-colors duration-200"
                                        >
                                            Reset
                                        </button>
                                        <button
                                            type="submit"
                                            className="px-4 py-2 bg-main-blue text-white rounded-lg hover:bg-main-blue/80 transition-colors duration-200 flex items-center gap-2"
                                        >
                                            <FaUpload className="text-sm" />
                                            {editModel ? 'Update Model' : 'Save Model'}
                                        </button>
                                    </div>
                                </form>
                            </div>
                        </div>

                        {/* AI Models List Card */}
                        <div className="bg-white rounded-xl shadow-sm overflow-hidden transition-all duration-300 hover:shadow-md border border-gray-100">
                            <div className="p-5 border-b border-gray-100">
                                <div className="flex items-center justify-between">
                                    <div className="flex items-center gap-3">
                                        <div className="p-2.5 bg-main-blue/10 rounded-lg">
                                            <FaRegEye className="text-main-blue text-xl" />
                                        </div>
                                        <div>
                                            <h2 className="text-lg font-semibold text-gray-800">AI Models</h2>
                                            <p className="text-xs text-gray-500 mt-0.5">Manage your existing AI models</p>
                                        </div>
                                    </div>
                                    {selectedModels.length > 0 && (
                                        <button
                                            onClick={handleDelete}
                                            className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors duration-200 flex items-center gap-2"
                                        >
                                            <FaTrashAlt className="text-sm" />
                                            Delete Selected ({selectedModels.length})
                                        </button>
                                    )}
                                </div>
                            </div>

                            <div className="p-5 bg-gray-50/50">
                                {models.length > 0 ? (
                                    <div className="overflow-x-auto">
                                        <table className="min-w-full divide-y divide-gray-200">
                                            <thead className="bg-main-th">
                                                <tr>
                                                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider rounded-tl-lg">
                                                        <input
                                                            type="checkbox"
                                                            className="h-4 w-4 text-main-blue focus:ring-main-blue border-gray-300 rounded"
                                                            onChange={handleSelectAll}
                                                            checked={selectedModels.length === models.length && models.length > 0}
                                                        />
                                                    </th>
                                                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                        Image
                                                    </th>
                                                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                        Model Name
                                                    </th>
                                                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                        URL
                                                    </th>
                                                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                        Status
                                                    </th>
                                                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider rounded-tr-lg">
                                                        Actions
                                                    </th>
                                                </tr>
                                            </thead>
                                            <tbody className="bg-white divide-y divide-gray-200">
                                                {models.map((model) => (
                                                    <tr key={model._id} className="hover:bg-main-thHover">
                                                        <td className="px-6 py-4 whitespace-nowrap">
                                                            <input
                                                                type="checkbox"
                                                                className="h-4 w-4 text-main-blue focus:ring-main-blue/80 border-gray-300 rounded"
                                                                onChange={() => handleSelectModel(model._id)}
                                                                checked={selectedModels.includes(model._id)}
                                                            />
                                                        </td>
                                                        <td className="px-6 py-4 whitespace-nowrap">
                                                            <div className="flex-shrink-0 h-10 w-10">
                                                                <img
                                                                    className="h-10 w-10 rounded-full object-cover"
                                                                    src={model.m_image}
                                                                    alt={model.m_name}
                                                                />
                                                            </div>
                                                        </td>
                                                        <td className="px-6 py-4 whitespace-nowrap">
                                                            <div className="text-sm font-medium text-gray-900">{model.m_name}</div>
                                                        </td>
                                                        <td className="px-6 py-4 whitespace-nowrap">
                                                            <div className="text-sm text-gray-500 max-w-xs truncate">
                                                                <a href={model.m_url} target="_blank" rel="noopener noreferrer" className="text-main-blue hover:underline">
                                                                    {model.m_url}
                                                                </a>
                                                            </div>
                                                        </td>
                                                        <td className="px-6 py-4 whitespace-nowrap">
                                                            <label className="relative inline-flex items-center cursor-pointer">
                                                                <input
                                                                    type="checkbox"
                                                                    checked={model.m_status}
                                                                    onChange={() => handleUpdateStatus(model._id)}
                                                                    className="sr-only peer"
                                                                />
                                                                <div className="relative w-11 h-6 bg-gray-200 rounded-full peer 
                                            peer-checked:after:translate-x-full peer-checked:after:border-white 
                                            after:content-[''] after:absolute after:top-0.5 after:start-[2px] 
                                            after:bg-white after:border-gray-300 after:border after:rounded-full 
                                            after:h-5 after:w-5 after:transition-all dark:border-gray-600 
                                            peer-checked:bg-main-blue"></div>
                                                            </label>
                                                        </td>
                                                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                                                            <button
                                                                onClick={() => handleEdit(model)}

                                                                className="flex items-center px-2 py-1 bg-main-blue/10 text-main-blue rounded-md hover:bg-main-blue/20 transition-colors"
                                                            >
                                                                <FaEdit />
                                                            </button>
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                ) : (
                                    <NotFound />
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};

export default AiModel;